import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

import Card from '../../../components/ui/Card';
import { useFPO } from './FPOContext';

export default function FPOOverview() {
  const { organisation, refresh } = useFPO();

  // Refresh once on mount so counts reflect changes made elsewhere this session.
  useEffect(() => {
    refresh().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <FPOOverviewBody organisation={organisation} />;
}

function FPOOverviewBody({ organisation }) {
  const stats = [
    { label: 'Member farmers', value: organisation.farmer_count ?? 0, to: '/app/farmers' },
    { label: 'Active listings', value: organisation.active_listings_count ?? 0, to: '/app/listings' },
    { label: 'Open orders', value: organisation.open_orders_count ?? 0, to: '/app/orders' },
  ];

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-green-700">
            {organisation.district}, {organisation.state}
          </p>
          <h1 className="text-2xl font-semibold text-gray-900">{organisation.name}</h1>
          {organisation.registration_number && (
            <p className="text-sm text-gray-500">Reg. {organisation.registration_number}</p>
          )}
        </div>
        <span
          className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider ${
            organisation.verified
              ? 'bg-green-100 text-green-700'
              : 'bg-amber-100 text-amber-700'
          }`}
        >
          <span className={`h-2 w-2 rounded-full ${organisation.verified ? 'bg-green-500' : 'bg-amber-500'}`} />
          {organisation.verified ? 'Verified' : 'Pending verification'}
        </span>
      </header>

      <div className="grid gap-4 sm:grid-cols-3">
        {stats.map((s) => (
          <Link to={s.to} key={s.label}>
            <Card className="h-full p-5 transition-shadow hover:shadow-md">
              <p className="text-xs uppercase tracking-wide text-gray-500">{s.label}</p>
              <p className="mt-2 text-3xl font-semibold text-gray-900">{s.value}</p>
              <p className="mt-1 text-xs text-green-700">Manage →</p>
            </Card>
          </Link>
        ))}
      </div>

      {organisation.about && (
        <Card className="p-6">
          <h2 className="text-base font-semibold text-gray-900">About</h2>
          <p className="mt-2 whitespace-pre-line text-sm text-gray-700">{organisation.about}</p>
        </Card>
      )}

      <Card className="p-6">
        <h2 className="text-lg font-semibold text-gray-900">Next up</h2>
        <ul className="mt-3 list-inside list-disc space-y-1 text-sm text-gray-700">
          <li>Add your member farmers and their farms.</li>
          <li>Create your first crop listing once you've aggregated stock.</li>
          <li>Buyers will start to discover your FPO and place orders.</li>
        </ul>
      </Card>
    </div>
  );
}
